import React from "react";
import { NavLink, Outlet, Navigate, Link, useNavigate } from "react-router-dom";
import { House, Hammer, Image, ArrowsLeftRight, Article, ChatCircle, EnvelopeSimple, Users, Gear, TextAa, SignOut, ArrowSquareOut } from "@phosphor-icons/react";
import { LOGO_URL } from "../../lib/api";
import { useAuth } from "../../lib/auth";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: House },
  { to: "/admin/services", label: "Services", icon: Hammer },
  { to: "/admin/gallery", label: "Gallery", icon: Image },
  { to: "/admin/before-after", label: "Before & After", icon: ArrowsLeftRight },
  { to: "/admin/blog", label: "Journal", icon: Article },
  { to: "/admin/testimonials", label: "Testimonials", icon: ChatCircle },
  { to: "/admin/contacts", label: "Messages", icon: EnvelopeSimple },
  { to: "/admin/content", label: "Page Content", icon: TextAa },
  { to: "/admin/users", label: "Admin Users", icon: Users },
  { to: "/admin/settings", label: "Settings", icon: Gear },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="admin-shell flex items-center justify-center">
        <div className="font-mono text-sm text-white/50 uppercase tracking-widest">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin" replace />;
  if (!user.is_admin) return <Navigate to="/admin?unauthorized=1" replace />;

  const doLogout = async () => {
    await logout();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="admin-shell" data-testid="admin-shell">
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] min-h-screen">
        <aside className="bg-[#141414] border-r border-[#2a2a2a] flex flex-col">
          <div className="p-6 border-b border-[#2a2a2a] flex items-center gap-3">
            <img src={LOGO_URL} alt="ATC" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-display text-lg leading-none text-white">ATC ADMIN</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 mt-1">Control panel</div>
            </div>
          </div>
          <nav className="flex-1 p-4 space-y-1" data-testid="admin-nav">
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 font-heading uppercase tracking-widest text-xs transition-colors ${
                    isActive ? "bg-atc-red text-white" : "text-white/70 hover:bg-[#1f1f1f] hover:text-white"
                  }`
                }
              >
                <Icon size={16} weight="bold" />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-[#2a2a2a] space-y-2">
            <Link to="/" target="_blank" className="flex items-center gap-2 text-xs font-heading uppercase tracking-widest text-white/60 hover:text-white" data-testid="view-site">
              <ArrowSquareOut size={14} weight="bold" /> View Site
            </Link>
            <div className="flex items-center gap-3 pt-2">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-8 w-8 object-cover border border-white/20" />
              ) : (
                <div className="h-8 w-8 bg-atc-red flex items-center justify-center font-bold">
                  {user.email?.[0]?.toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-white truncate" data-testid="current-user-email">{user.email}</div>
                <button onClick={doLogout} className="text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-atc-red flex items-center gap-1" data-testid="logout-btn">
                  <SignOut size={10} weight="bold" /> Logout
                </button>
              </div>
            </div>
          </div>
        </aside>

        <main className="min-w-0 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
